import { FormEvent, useState } from 'react';
import { useStore } from 'react-redux';

import { OOPages, OOSteps } from '@features/oneOnboarding/interfaces';
import { mutateUserFlowStepChange } from '@hooks/apiHooks';
import { useDecodedParams } from '@hooks/useDecodedParams';
import { OnboardingStatus } from '@models/onboarding.model';
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { RootState } from '@store/rootReducer';
import { useQueryClient } from '@tanstack/react-query';

export type ChangeStepModalPropsType = {
  isActive: boolean;
  onboardingStatus: string | undefined;
  closeModal: () => void;
};

const ChangeFlowStepModal = ({ isActive, onboardingStatus, closeModal }: ChangeStepModalPropsType) => {
  const queryClient = useQueryClient();
  const store = useStore<RootState>();
  const {
    flow,
    isCompleted,
    currentForm,
    currentStep: currentStepFlow,
    lastStepCompleted: lastFlowStepCompleted,
  } = store.getState().oneOnboarding;
  const { clientOrganizationId, userId, applicationId, configurationId } = useDecodedParams();
  const [currentStep, setCurrentStep] = useState<OOSteps>(currentStepFlow);
  const [currentPage, setCurrentPage] = useState<OOPages | null>(currentForm);
  const [lastStepCompleted, setLastStepCompleted] = useState<OOSteps | string>(lastFlowStepCompleted);
  const [isFlowCompleted, setIsFlowCompleted] = useState<boolean | undefined>(isCompleted);
  const [status, setStatus] = useState(onboardingStatus);
  const stepsArray = flow?.map((step) => step.name);
  const pagesArray = flow?.find((step) => step.name === currentStep)?.pages.map((page) => page.name);

  const { mutate: updateUserFlowStep, error, isError, isLoading }: any = mutateUserFlowStepChange(
    clientOrganizationId,
    configurationId,
    userId,
    applicationId,
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getOnboardingsWithRelationsMultiple', clientOrganizationId, configurationId, applicationId, userId],
      });
      queryClient.invalidateQueries({
        queryKey: ['getUserFlow', clientOrganizationId, configurationId, userId, applicationId],
      });
    },
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payload = {
      currentStep,
      lastStepCompleted,
      isCompleted: isFlowCompleted,
      status,
    };
    if (currentPage) {
      Object.assign(payload, { currentPage });
    }
    updateUserFlowStep(payload);
  };

  return (
    <Dialog open={isActive} onClose={closeModal}>
      <Box>
        <DialogTitle>
          <p className="tag-ds  h4">Update candidate step</p>
          {isError && <div className="tag-ds error-message">Error: {JSON.stringify(error)}</div>}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <div className="tag-ds input-wrapper" style={{ marginBottom: '20px' }}>
              <label htmlFor="currentStep">Current step</label>
              <select
                name="currentStep"
                id="currentStep"
                value={currentStep}
                onChange={(e) => {
                  setCurrentStep(e.target.value as OOSteps);
                  setCurrentPage(null);
                }}
              >
                {stepsArray.map((step) => (
                  <option key={step} value={step}>
                    {step}
                  </option>
                ))}
              </select>
            </div>

            <div className="tag-ds input-wrapper" style={{ marginBottom: '20px' }}>
              <label htmlFor="currentPage">Current page</label>
              <select
                name="currentPage"
                id="currentPage"
                onChange={(e) => setCurrentPage(e.target.value as OOPages)}
                disabled={pagesArray?.length === 0}
                value={currentPage ? currentPage : ''}
              >
                <option value="">No value</option>
                {pagesArray?.map((page) => (
                  <option key={page} value={page}>
                    {page}
                  </option>
                ))}
              </select>
            </div>

            <div className="tag-ds input-wrapper" style={{ marginBottom: '20px' }}>
              <label htmlFor="lastStepCompleted">Last step completed</label>
              <select
                name="lastStepCompleted"
                id="lastStepCompleted"
                onChange={(e) => setLastStepCompleted(e.target.value as OOSteps)}
                value={lastStepCompleted}
              >
                {stepsArray.map((step) => (
                  <option key={step} value={step}>
                    {step}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <input
                type="checkbox"
                id="flowCompleted"
                style={{ marginRight: '10px' }}
                checked={isFlowCompleted ? true : false}
                onChange={(e) => setIsFlowCompleted(e.target.checked)}
              />
              <label htmlFor="flowCompleted">Flow completed</label>
            </div>

            <div className="tag-ds input-wrapper" style={{ marginBottom: '20px' }}>
              <label htmlFor="onboarding-status">Onboarding status</label>
              <select name="status" id="onboarding-status" onChange={(e) => setStatus(e.target.value)} value={status}>
                {Object.keys(OnboardingStatus).map((stats) => (
                  <option key={stats} value={stats}>
                    {stats}
                  </option>
                ))}
              </select>
            </div>

            <div className="tag-ds input-wrapper" style={{ marginTop: '20px' }}>
              <button type="submit">{isLoading ? <CircularProgress size={20} color="secondary" /> : 'Submit'}</button>
            </div>
          </form>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default ChangeFlowStepModal;
